<template>
  <div class="pricing-page" :class="[$config.project]">
    <Hero :stats="pageData.stats" :plan-specs-cards="planSpecsCards" />
    <section v-if="videos && videos.length" class="white slider videos" data-event="videos.scroll">
      <div class="container">
        <div class="heading">
          <h2 class="h3">
            Inspiring Success Stories from Our Graduates
          </h2>
          <p class="subtitle">
            Hear from students who achieved their career aspirations by leveraging 365 Data Science.
          </p>
        </div>
        <Videos
          v-if="$config.platform === 'ds'" :videos="videos" :video-js-loaded="videoJsLoaded"
          light
        />
      </div>
    </section>
    <section id="compare-features" class="features container">
      <h2 class="h3">
        A Detailed 365 Data Science Plan Comparison
      </h2>
      <p class="subtitle">
        Enjoy our high-quality training for a fraction of the cost of traditional data science education. Compare the 365 Data Science Pricing Plan features and select the best one for your needs and goals with the flexibility to <strong>upgrade anytime</strong>.
      </p>
      <div class="content">
        <div class="row sticky">
          <div class="col col-4" />
          <div v-if="$config.project === 'site'" class="col col-2 free">
            <div class="title">
              &nbsp;<br>Free Plan
            </div>
            <span class="billed">
              &nbsp;
            </span>
            <div class="price">
              <span class="old-price empty">
                &nbsp;
              </span>
              <span class="discount-price">
                {{ '0' | formatCurrency(currency, { minimumFractionDigits: 0, dollarVariation: false }) }}
              </span>
            </div>
            <nuxt-link to="/signup/" class="btn-primary btn-hollow" data-event="table_free_v1.click">
              Get Started
            </nuxt-link>
          </div>
          <div class="col monthly" :class="{ 'col-2': $config.project === 'site', 'col-auto': $config.project === 'lms' }">
            <div class="title">
              Premium<br>Monthly
            </div>
            <span class="billed">
              Billed Monthly
            </span>
            <div class="price">
              <span class="old-price empty">
                &nbsp;
              </span>
              <span class="discount-price">
                {{ (billedMonthly.discount || billedMonthly.regular) | formatCurrency(currency, { minimumFractionDigits: 0, dollarVariation: false }) }}
                <span class="month">
                  /month
                </span>
              </span>
            </div>
            <nuxt-link
              v-if="$config.project === 'site'" to="/checkout/?plan=monthly" class="btn-primary btn-hollow btn-clone"
              data-event="table_monthly_v1.click"
            >
              Get Started
            </nuxt-link>
            <button
              v-else class="btn-primary btn-hollow btn-clone" data-target="monthly"
              data-event="table_monthly_v1.click"
            >
              Get Started
            </button>
          </div>
          <div v-if="$config.project === 'lms'" class="col col-auto quarterly">
            <div class="title">
              Premium<br>Quarterly
            </div>
            <span class="billed">
              Billed Quarterly <span v-if="billedQuarterly.save && $config.project === 'lms'" class="save-label"> {{ billedQuarterly.save }} </span>
            </span>
            <div class="price">
              <span class="old-price">
                {{ billedMonthly.regular | formatCurrency(currency, { minimumFractionDigits: digits((billedAnnual.discount || billedAnnual.regular)), minimumFractionDigits: 0, dollarVariation: false }) }}
              </span>
              <span class="discount-price">
                {{ (billedQuarterly.discount || billedQuarterly.regular) | formatCurrency(currency, { minimumFractionDigits: 0, dollarVariation: false }) }}
                <span class="month">
                  /month
                </span>
              </span>
            </div>
            <nuxt-link
              v-if="$config.project === 'site'" to="/checkout/?plan=quarterly" class="btn-primary btn-hollow btn-clone"
              data-event="table_quarterly_v1.click"
            >
              Get Started
            </nuxt-link>
            <button
              v-else class="btn-primary btn-hollow btn-clone" data-target="quarterly"
              data-event="table_quarterly_v1.click"
            >
              Get Started
            </button>
          </div>
          <div class="col annual" :class="{ 'col-2': $config.project === 'site', 'col-auto': $config.project === 'lms' }">
            <div class="title">
              Premium<br>Annual
            </div>
            <span class="billed">
              Billed Annually
            </span>
            <div class="price">
              <span class="old-price">
                {{ billedMonthly.regular | formatCurrency(currency, { minimumFractionDigits: digits((billedAnnual.discount || billedAnnual.regular)), dollarVariation: false }) }}
              </span>
              <span class="discount-price">
                {{ (billedAnnual.discount || billedAnnual.regular) | formatCurrency(currency, { minimumFractionDigits: digits((billedAnnual.discount || billedAnnual.regular)), dollarVariation: false }) }}
                <span class="month">
                  /month
                </span>
              </span>
            </div>
            <nuxt-link
              v-if="$config.project === 'site'" to="/checkout/?plan=annual" class="btn-primary btn-clone"
              data-event="table_annual_v1.click"
            >
              Get Started
            </nuxt-link>
            <button
              v-else class="btn-primary btn-clone" data-target="annual"
              data-event="table_annual_v1.click"
            >
              Get Started
            </button>
            <span class="label">
              {{ billedAnnual.save }}
            </span>
          </div>
          <div v-if="$config.project === 'site'" class="col col-2 business">
            <div class="title">
              Business<br>Plan
            </div>
            <span class="billed">
              Custom<br>Price
            </span>
            <nuxt-link to="/request-demo/" class="btn-primary-yellow btn-hollow" data-event="table_business_v1.click">
              Request a Demo
            </nuxt-link>
          </div>
        </div>
        <div
          v-for="(spec, idx) in planSpecsMixed" :key="idx" class="row"
          :class="spec.class || 'spec'"
        >
          <div
            v-for="(col, idx2) in spec.cols" :key="idx2" class="col"
            :class="[
              'col', col.class,
              {
                check: col.check,
                'col-4': idx2 === 0,
                'col-2': idx2 !== 0 && $config.project === 'site',
                'col-auto': $config.project === 'lms' && idx2 !== 0
              }
            ]"
          >
            <IconPricingCheck v-if="col.check" />
            <span v-else v-html="col.name || '&nbsp;'" />
          </div>
        </div>
      </div>
      <div class="features-footer">
        <Badges />
        <p class="vat">
          VAT may be applied
        </p>
      </div>
    </section>
    <Lazy never>
      <Reviews
        dark class="tracking-section" data-event="reviews.scroll"
        :title="`Over ${$c.text('students.count')} Students Worldwide and 413,000+ Reviews`"
      />
    </Lazy>
    <Lazy :when-visible="{ rootMargin: '200px' }">
      <FrequentlyAskedQuestions
        group="pricing" class="site-section tracking-section" :results="pageData.faqResults"
        data-event="faq.scroll"
      />
    </Lazy>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import { mapState, mapGetters } from 'vuex';
import Lazy from 'vue-lazy-hydration';
import { calculateBilledPerMonth, loadHotJar } from '../utils/helper';
import FrequentlyAskedQuestions from '../FrequentlyAskedQuestions';
import Badges from './Badges';
import Hero from './PricingHeroV2';
import Reviews from '../Reviews';
import Videos from '../Campaigns/Videos';
import IconPricingCheck from '../assets/svg/icon-pricing-check.svg?vue-component';

export default {
  components: {
    Hero,
    Lazy,
    Videos,
    Reviews,
    FrequentlyAskedQuestions,
    Badges,
    IconPricingCheck,
  },
  props: {
    pageData: { type: Object, default: () => ({}), required: true },
  },
  data() {
    return {
      videoJsLoaded: false,
      planSpecs: [
        {
          class: 'group',
          cols: [
            { name: 'Learning Materials' },
            { name: '' },
            { name: '' },
            { name: '' },
            { name: '' },
          ],
        },
        {
          cols: [
            { name: 'Access to the entire Courses library' },
            { name: 'Limited to 820 lessons', class: '' },
            { name: 'All 77 courses,<br>3508 lessons', class: '' },
            { name: 'All 77 courses,<br>3508 lessons', class: '' },
            { name: 'All 77 courses,<br>3508 lessons', class: '' },
          ],
        },
        {
          cols: [
            { name: 'New content' },
            { name: '' },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Content updates' },
            { name: '' },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Structured career tracks' },
            { name: '' },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Custom learning path' },
            { name: '' },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Hands-on exercises' },
            { name: 'Limited to<br>207 exercises', class: '' },
            { name: 'All 672<br>exercises', class: '' },
            { name: 'All 672<br>exercises', class: '' },
            { name: 'All 672<br>exercises', class: '' },
          ],
        },
        {
          cols: [
            { name: 'Downloadable resources' },
            { check: true },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Practice exams' },
            { check: true },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Certification exams' },
            { name: '' },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          class: 'group',
          cols: [
            { name: 'Employability Boosters' },
            { name: '' },
            { name: '' },
            { name: '' },
            { name: '' },
          ],
        },
        {
          cols: [
            { name: 'Certificates of achievement' },
            { name: '' },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Real-world data projects' },
            { name: 'Limited to 5<br>free projects' },
            { name: 'Limited to 5<br>free projects' },
            { name: 'All 23 projects' },
            { name: 'All 23 projects' },
          ],
        },
        {
          cols: [
            { name: 'Resume builder' },
            { check: true },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Resume feedback' },
            { name: '' },
            { name: '' },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Portfolio advice' },
            { name: '' },
            { name: '' },
            { check: true },
            { check: true },
          ],
        },
        {
          class: 'group',
          cols: [
            { name: 'Platform Features' },
            { name: '' },
            { name: '' },
            { name: '' },
            { name: '' },
          ],
        },
        {
          cols: [
            { name: 'Social channels' },
            { check: true },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Gamified experience' },
            { check: true },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Leaderboard' },
            { name: '' },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'XPs and leveling up' },
            { check: true },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Card collections with hidden benefits' },
            { check: true },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          cols: [
            { name: 'Rewards for the most avid learners' },
            { check: true },
            { check: true },
            { check: true },
            { check: true },
          ],
        },
        {
          class: 'group',
          cols: [
            { name: 'Support' },
            { name: '' },
            { name: '' },
            { name: '' },
            { name: '' },
          ],
        },
        {
          cols: [
            { name: 'Personalized support' },
            { name: '' },
            { name: '' },
            { check: true },
            { name: '' },
          ],
        },
        {
          business: true,
          cols: [
            { name: 'Dedicated support agent' },
            { name: '' },
            { name: '' },
            { name: '' },
            { check: true },
          ],
        },
        {
          business: true,
          class: 'group',
          cols: [
            { name: 'Business Features' },
            { name: '' },
            { name: '' },
            { name: '' },
            { name: '' },
          ],
        },
        {
          business: true,
          cols: [
            { name: 'Admin dashboard' },
            { name: '' },
            { name: '' },
            { name: '' },
            { check: true },
          ],
        },
        {
          business: true,
          cols: [
            { name: 'Subscription management' },
            { name: '' },
            { name: '' },
            { name: '' },
            { check: true },
          ],
        },
        {
          business: true,
          cols: [
            { name: 'Task management' },
            { name: '' },
            { name: '' },
            { name: '' },
            { check: true },
          ],
        },
        {
          business: true,
          cols: [
            { name: 'Advanced reporting' },
            { name: '' },
            { name: '' },
            { name: '' },
            { check: true },
          ],
        },
        {
          business: true,
          cols: [
            { name: 'Data export' },
            { name: '' },
            { name: '' },
            { name: '' },
            { check: true },
          ],
        },
        {
          business: true,
          cols: [
            { name: 'Priority onboarding' },
            { name: '' },
            { name: '' },
            { name: '' },
            { check: true },
          ],
        },
        {
          business: true,
          cols: [
            { name: 'Access to talents' },
            { name: '' },
            { name: '' },
            { name: '' },
            { check: true },
          ],
        },
      ],
      planSpecsCards: [
        { name: 'Limited to 820 lessons', plan: ['free'] },
        { name: 'All 77 courses, 3508 lessons', plan: ['monthly', 'annual', 'business'] },
        { name: 'All 672 exercises', plan: ['monthly', 'annual', 'business'] },
        { name: 'Limited to 207 exercises', plan: ['free'] },
        { name: 'Limited to 5 free projects', plan: ['free', 'monthly'] },
        { name: 'All 23 projects', plan: ['annual', 'business'] },
        { name: 'Exams', plan: ['business'] },
        { name: 'Certificates of achievement', plan: ['business'] },
        { name: 'Downloadable resources', plan: ['all'] },
        { name: 'Social channels', plan: ['business'] },
        { name: 'Practice exams', plan: ['free', 'monthly', 'annual'] },
        { name: 'Resume builder', plan: ['free', 'monthly', 'annual'] },
        { name: 'Social channels', plan: ['free', 'monthly', 'annual'] },
        { name: 'Gamified features', plan: ['all'] },
        { name: 'Leaderboard', plan: ['all'], notChecked: ['free'] },
        { name: 'New content', plan: ['all'], notChecked: ['free'] },
        { name: 'Content updates', plan: ['all'], notChecked: ['free'] },
        { name: 'Structured career tracks', plan: ['all'], notChecked: ['free'] },
        { name: 'Custom learning path', plan: ['all'], notChecked: ['free'] },
        { name: 'Certification exams', plan: ['free', 'monthly', 'annual'], notChecked: ['free'] },
        { name: 'Certificates of achievement', plan: ['free', 'monthly', 'annual'], notChecked: ['free'] },
        { name: 'Resume feedback', plan: ['free', 'monthly', 'annual'], notChecked: ['free', 'monthly'] },
        { name: 'Portfolio advice', plan: ['free', 'monthly', 'annual'], notChecked: ['free', 'monthly'] },
        { name: 'Personalized support', plan: ['free', 'monthly', 'annual'], notChecked: ['free', 'monthly'] },
        { name: 'Priority onboarding', plan: ['business'] },
        { name: 'Access to talents', plan: ['business'] },
        { name: 'Admin dashboard', plan: ['business'] },
        { name: 'Task management', plan: ['business'] },
        { name: 'Advanced reporting', plan: ['business'] },
        { name: 'Data export', plan: ['business'] },
        { name: 'Dedicated support agent', plan: ['business'] },
      ],
      videos: [
        {
          name: 'Lee McFarling',
          image: 'lee',
          from: 'USA',
          id: 6315230915112,
          quote: 'After 6 months of learning with 365 Data Science, I was getting job interviews as a business analyst or a data analyst.',
          job: 'Data Analyst, Astreya',
        },
        {
          name: 'Meghan Verschoor',
          image: 'meghan',
          from: 'Canada',
          id: 6315228912112,
          quote: 'I find the platform really easy to follow through with and learn the skills that I needed to apply to my job. I highly recommend it.',
          job: 'Director, Data Science & Management, Turnstone Biologics',
        },
        {
          name: 'Nada Abdulwahab',
          image: 'nada',
          from: 'Egypt',
          id: 6315228795112,
          quote: '365 gave me the basics to start my career […] and still helps me enhance my skills and push my career to a higher level.',
          job: 'Senior Data Engineer, VOIS',
        },
        {
          name: 'Julián Elizondo',
          image: 'julián',
          from: 'Mexico',
          id: 6343656185112,
          quote: 'If it wasn’t for 365 Data Science and the confidence it gave me, I wouldn’t have landed my current job.',
          job: 'Tableau Developer, Schneider Electric',
        },
        {
          name: 'Mihaela Paparinova',
          image: 'mihaela',
          from: 'Bulgaria',
          id: 6341101204112,
          quote: 'The program helped me excel in my studies and deepen my understanding of niche data science concepts.',
          job: 'Data Science Student, Sofia University',
        },
        {
          name: 'Ernest Asena',
          image: 'ernest',
          from: 'Kenya',
          id: 6315231920112,
          quote: 'Eight months into the program, I started applying for entry-level positions, and I landed a job as a junior data scientist.”',
          job: 'Data Insights Analytics Cloud Engineer, AWS',
        },
        {
          name: 'Ahmed Eldessouky',
          image: 'ahmed',
          from: 'Egypt',
          id: 6327293863112,
          quote: 'I was impressed with the quality of the video lessons, and the topics and different tools the program covers.',
          job: 'Business Developer, Squadio',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('subscriptions', ['monthly', 'quarterly', 'annual']),
    ...mapState('subscriptions', ['currency']),
    billedMonthly() {
      return this.billedPerMonth(this.monthly);
    },
    billedQuarterly() {
      return this.billedPerMonth(this.quarterly);
    },
    billedAnnual() {
      return this.billedPerMonth(this.annual);
    },
    planSpecsMixed() {
      if (this.$config.project === 'lms') {
        return this.planSpecs.filter((spec) => !spec.business).map((spec) => {
          if (spec.class && spec.class === 'group') {
            return { ...spec, cols: spec.cols.slice(0, -1) };
          }

          const cols = spec.cols.filter((col, idx2) => (idx2 !== 1 && idx2 !== 4));

          return {
            cols: [...cols.slice(0, 2), { ...cols[1] }, ...cols.slice(2, 3)],
          };
        });
      }

      return this.planSpecs;
    },
  },
  async mounted() {
    const script = document.createElement('script');
    script.src = 'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js';
    script.async = true;
    document.body.appendChild(script);
    script.onload = () => {
      this.videoJsLoaded = true;
    };

    if (this.$config.platform === 'ds') {
      loadHotJar();
    }

    if (this.$config.project === 'lms') {
      this.clonePricingButtons();
    }
  },
  methods: {
    billedPerMonth(p) {
      const billedPerMonthValues = calculateBilledPerMonth(p, {
        active: this.$store.getters['subscriptions/active'] || {},
        monthly: this.monthly,
        quarterly: this.quarterly,
        annual: this.annual,
      }, this.sale);

      if (this.campaign && this.discount) {
        billedPerMonthValues.save = `Save ${this.discount}%`;
        billedPerMonthValues.get = `Get ${this.discount}% Off`;
      } else if (this.monthly.price > billedPerMonthValues.regular) {
        const save = Math.round(((this.monthly.price - billedPerMonthValues.regular) / this.monthly.price) * 100);
        billedPerMonthValues.save = `Save ${save === 19 ? 20 : save}%`;
        billedPerMonthValues.get = `Get ${save}% Off`;
      }

      return billedPerMonthValues;
    },
    digits(v) {
      return Number.isInteger(v) ? 0 : 2;
    },
    clonePricingButtons() {
      const buttons = document.querySelectorAll('.pricing-page .btn-clone');
      const cards = document.querySelector('.pricing-page .cards');
      buttons.forEach((button) => {
        const cardButton = cards.querySelector(`.card.${button.dataset.target} .btn-primary`);
        if (cardButton) {
          if (cardButton.disabled) {
            button.disabled = true;
            button.classList.add('disabled');
          } else {
            button.addEventListener('click', (e) => {
              e.preventDefault();
              cardButton.click();
            });
          }

          button.textContent = cardButton.textContent;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.site-section {
  padding: var(--spacing-80) 0;

  .text-center {
    text-align: center;
  }

  .text-right {
    text-align: right;
  }
}

.videos {
  padding: 100px 0;
  background-color: var(--main-light-shade-1);

  .heading {
    text-align: center;

    .h3 {
      margin: 0;
    }

    .subtitle {
      margin-top: var(--spacing-16);
      color: var(--main-dark-light-4);
    }
  }
}

.graduates-thoughts ::v-deep .videos {
  .heading .subtitle {
    margin-bottom: 0;
  }

  .slider-container.landing .slider-header {
    margin-bottom: var(--spacing-36);
  }

  .single-video {
    .name {
      color: var(--main-dark);
    }

    .job {
      color: var(--main-dark-light-2);
      font-size: 14px;
      line-height: 18px;
      margin: 4px 0;
    }

    .from {
      color: var(--main-dark-light-4);
    }

    .quote {
      margin: var(--spacing-16) 0 0;
      font-size: 14px;
      line-height: 18px;

      svg {
        display: block;
        margin-bottom: 4px;
      }
    }
  }
}

.features {
  padding: 100px 0;

  .sticky {
    position: sticky;
    top: 80px;
    z-index: 1;
    background-color: var(--main-light);
    padding-top: 70px;
  }

  .h3 {
    text-align: center;
    margin-bottom: var(--spacing-16);
  }

  .subtitle {
    text-align: center;
    color: var(--main-dark-light-4);
  }

  .content {
    margin-bottom: 70px;

    .row {
      .col:nth-child(4) {
        background-color: var(--accent-light-1);
        border-left: 1px solid var(--accent);
        border-right: 1px solid var(--accent);
      }

      &:first-child .col:nth-child(4) {
        border-top: 1px solid var(--accent);
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
      }

      &:last-child .col:nth-child(4) {
        border-bottom: 1px solid var(--accent);
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
      }
    }

    .title {
      text-align: center;
      font-size: 21px;
      line-height: 23px;
      font-weight: 500;
    }

    .billed {
      display: block;
      text-align: center;
      color: var(--main-dark-light-4);
      margin-top: 8px;
    }

    .price {
      font-size: 20px;
      margin: 8px auto 12px;
      width: fit-content;
      max-width: 100%;
      height: 46px;
      line-height: normal;

      .old-price {
        font-size: 14px;
        font-weight: 500;
        color: var(--main-dark-light-3);
        position: relative;

        &:not(.empty):after {
          content: '';
          position: absolute;
          width: 100%;
          height: 1px;
          left: 0;
          top: 50%;
          background-color: var(--main-dark-light-3);
        }
      }

      .discount-price {
        font-weight: 500;
        display: block;

        .month {
          font-weight: 400;
        }
      }

      .month {
        font-size: 16px;
        font-weight: 500;
      }
    }

    .btn-primary,
    .btn-primary-yellow {
      width: 100%;
      height: 40px;
      padding: 12px 0;
      margin-bottom: 18px;
      font-size: 16px;
    }

    .annual {
      position: relative;
      padding-top: 24px;
      margin-top: -24px;

      .title {
        color: var(--accent-shade-2);
      }

      .label {
        position: absolute;
        left: 50%;
        margin-left: -40px;
        width: 80px;
        height: 28px;
        line-height: 28px;
        background-color: var(--main-yellow);
        color: var(--main-dark);
        top: -16px;
        border-radius: 3px;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
      }
    }

    .business {
      .billed {
        font-size: 18px;
        height: 46px;
        margin: 36px 0 12px;
      }
    }

    .group {
      .col {
        &:first-child {
          font-size: 24px;
          font-weight: 500;
        }

        &:first-child,
        &:nth-child(4) {
          padding-top: 36px;
          padding-bottom: 12px;
        }
      }
    }

    .row.spec {
      color: var(--main-dark-light-3);
      font-weight: 400;
      display: flex;
      align-items: center;

      + .row.spec {
        border-top: 1px solid var(--main-light-shade-4);
      }

      .col:nth-child(4) {
        height: 42px;
      }

      .col + .col {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 14px;
        font-weight: 400;

        &.check {
          color: var(--accent-shade-2);
        }
      }
    }
  }

  .features-footer {
    color: var(--main-dark-light-4);
    font-size: 18px;
    font-weight: 400;
    text-align: center;

    .vat {
      margin: var(--spacing-16) 0 0;
    }
  }
}

.pricing-page.lms {
  .sticky {
    top: 64px;
    background-color: var(--main-light-shade-1);
  }

  .features {
    .content {
      .billed {
        font-size: 14px;
      }

      .save-label {
        line-height: 24px;
        background-color: var(--accent-light-2);
        color: var(--accent-shade-2);
        font-size: 14px;
        border-radius: 3px;
        padding: 0 8px;
        display: inline-block;
        font-weight: 500;
      }

      .btn-primary,
      .btn-primary-yellow {
        max-width: 170px;
        display: block;
        margin-left: auto;
        margin-right: auto;
      }

      .row.spec .col {
        height: 48px;
      }
    }
  }

  .btn-primary:disabled:hover {
    color: var(--accent);
  }

  ::v-deep {
    :not(h1).h2,
    .h3 {
      font-size: 32px;
      line-height: 1.1;
    }

    .subtitle {
      font-size: 16px;
    }

    a {
      color: var(--link-color);
      cursor: pointer;
      text-decoration: none;
      transition: all var(--duration) ease-in;

      &:hover {
        color: var(--accent-shade-3);
      }

      &:active {
        color: var(--link-color);
        transition-duration: 0ms;
      }

      &.link {
        color: var(--main-light);

        &:hover {
          color: var(--main-light-shade-4);
        }

        &:active {
          color: var(--main-light-shade-2);
          transition-duration: 0ms;
        }
      }

      &.btn-link {
        color: var(---main-light-shade-1);
        cursor: default;

        &:hover {
          color: var(--main-light-shade-2);
        }

        &:active {
          color: var(--main-light-shade-1);
          transition-duration: 0ms;
        }
      }
    }
  }
}

@include bp($bp-laptop) {
  .features {
    .content {
      .title {
        font-size: 18px;
      }

      .btn-primary,
      .btn-primary-yellow {
        font-size: 14px;
      }
    }
  }

  .pricing-page.lms {
    ::v-deep {
      h1 {
        font-size: 32px;
      }

      :not(h1).h2,
      .h3 {
        font-size: 28px;
        line-height: 1.1;
      }
    }
  }
}

@include bp($bp-mobile) {
  .features {
    display: none;
  }
}
</style>
